import {
  fetchInitialChatState,
  fetchLimitedInitialChatState,
  fetchThreadState,
} from '@/store/thunks/FetchThreads';
import { getLayoverLogger } from '@/logging/LayoverLogger';
import {
  GetThreadResponseBody,
  GetUserThreadsResponseBody,
} from '@/api/ApiResponse';
import {
  handleFetchThreadCompleteReducer,
  handleFetchThreadErrorReducer,
  handleFetchThreadStartingReducer,
  handleFetchUserThreadsCompleteReducer,
  handleFetchUserThreadsErrorReducer,
  handleFetchUserThreadsStartingReducer,
} from '@/store/reducers/ChatStoreHelpers';
import { Message, NewMessage } from '@/models/Message';
import { Thread, ThreadId } from '@/models/Thread';

import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidV4 } from 'uuid';

export enum LOADING_STATE {
  'NOT_STARTED' = 'NOT_STARTED',
  'LOADING' = 'LOADING',
  'LOADED' = 'LOADED',
  'ERROR' = 'ERROR',
}

export type ThreadLoadingState = {
  isInitialized: boolean;
  loadingState: LOADING_STATE;
};

export interface ChatStoreState {
  initializeThreadsState: LOADING_STATE;
  threads: Record<ThreadId, Thread>;
  threadsLoadingStates: Record<ThreadId, ThreadLoadingState>;
}

const initialState: ChatStoreState = {
  initializeThreadsState: LOADING_STATE.NOT_STARTED,
  threads: {},
  threadsLoadingStates: {},
};

export interface FetchThreadsPayload {
  type: string;
  threadsResponse?: GetUserThreadsResponseBody | null;
  error?: string;
}

export interface FetchThreadPayload {
  type: string;
  threadResponse?: GetThreadResponseBody | null;
  error?: string;
}

export const createThreadReducer = (
  state: ChatStoreState,
  action: PayloadAction<Thread>,
): ChatStoreState => {
  const thread: Thread = action.payload;

  return {
    ...state,
    threads: {
      ...state.threads,
      [thread.id]: thread,
    },
    threadsLoadingStates: {
      ...state.threadsLoadingStates,
      [thread.id]: {
        isInitialized: false,
        loadingState: LOADING_STATE.LOADED,
      },
    },
  };
};

export const sendMessageReducer = (
  state: ChatStoreState,
  action: PayloadAction<{ message: Message | NewMessage; thread: Thread }>,
): ChatStoreState => {
  const { message, thread } = action.payload;

  const messageWithId =
    'id' in message ? message : { ...message, id: uuidV4() };
  const threadId = message.threadId;
  const existingThread = state.threads[threadId];
  // the thread arg is only used if its a new thread
  const updatedThread = existingThread ?? thread;

  // Add or replace message in thread
  const messages = [...(updatedThread.messages ?? [])];
  const index = updatedThread.messages.findIndex(
    (msg: Message) => msg.id === messageWithId.id,
  );
  if (index < 0) {
    messages.push(messageWithId);
  } else {
    messages[index] = messageWithId;
  }

  return {
    ...state,
    threads: {
      ...state.threads,
      [threadId]: {
        ...updatedThread,
        messages,
      },
    },
  };
};

export const updateMessageReducer = (
  state: ChatStoreState,
  action: PayloadAction<{
    message: Message;
  }>,
): ChatStoreState => {
  const { message } = action.payload;
  const messageId = message.id;
  const threadId = message.threadId;
  const existingThread = state.threads[threadId];
  if (!existingThread) {
    getLayoverLogger().logError(
      `Thread with id ${threadId} not found in state when updating message with id ${messageId}`,
    );
    return state;
  }

  const updatedMessages = state.threads[threadId].messages.map((oldMessage) =>
    oldMessage.id === messageId ? message : oldMessage,
  );

  return {
    ...state,
    threads: {
      ...state.threads,
      [threadId]: {
        ...existingThread,
        messages: updatedMessages,
      },
    },
  };
};

export const updateThreadReducer = (
  state: ChatStoreState,
  action: PayloadAction<{
    thread: Thread;
  }>,
): ChatStoreState => {
  const { thread } = action.payload;
  const threadId = thread.id;
  return { ...state, threads: { ...state.threads, [threadId]: thread } };
};

export const deleteThreadReducer = (
  state: ChatStoreState,
  action: PayloadAction<ThreadId>,
): ChatStoreState => {
  const threadId = action.payload;
  const threads = { ...state.threads };
  delete threads[threadId];
  return { ...state, threads };
};

// selectors

const getThreads = (state: ChatStoreState) => state.threads;

const getThreadByIdSelector = (
  state: ChatStoreState,
  threadId: ThreadId | null,
): Thread | null => (threadId ? state.threads[threadId] ?? null : null);

const getMemoizedMessageKey = (state: ChatStoreState) => {
  const { threads } = state;
  return Object.values(threads).reduce((acc, thread) => {
    // Concatenate relevant thread properties
    return (
      acc +
      thread.id +
      '-' +
      thread.messages[thread.messages.length - 1]?.id +
      '-'
    );
  }, '');
};

// Memoized selector function to get ordered threads
export const getOrderedThreadsSelector = createSelector(
  getThreads,
  getMemoizedMessageKey,
  (threads: Record<ThreadId, Thread>, _: string) => {
    // Compute ordered threads
    const orderedThreads = Object.values(threads).sort((a, b) => {
      const aLastMessage = a.messages?.[a.messages.length - 1];
      const bLastMessage = b.messages?.[b.messages.length - 1];

      const aTimestamp = aLastMessage?.timestamp || 0;
      const bTimestamp = bLastMessage?.timestamp || 0;

      return bTimestamp - aTimestamp;
    });
    return orderedThreads;
  },
);

// Memoized selector function to get messages for a thread
export const getMessagesForThreadSelector = createSelector(
  getThreads,
  (_, threadId: ThreadId | null | undefined) => threadId,
  (threads, threadId) => {
    if (!threadId) {
      return [];
    }

    return threads[threadId]?.messages || [];
  },
);

const getThreadLoadingStateSelector = (
  state: ChatStoreState,
  threadId: ThreadId | null,
): ThreadLoadingState | null =>
  threadId ? state.threadsLoadingStates[threadId] ?? null : null;

export const chatStoreSlice = createSlice({
  name: 'chatStore',
  initialState,
  reducers: {
    createThread: createThreadReducer,
    deleteThread: deleteThreadReducer,
    sendMessage: sendMessageReducer,
    updateMessage: updateMessageReducer,
    updateThread: updateThreadReducer,
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchInitialChatState.pending,
        handleFetchUserThreadsStartingReducer,
      )
      .addCase(
        fetchInitialChatState.fulfilled,
        handleFetchUserThreadsCompleteReducer,
      )
      .addCase(
        fetchInitialChatState.rejected,
        handleFetchUserThreadsErrorReducer,
      )
      // TODO (#932): remove fetchLimitedInitialChatState cases once we completely switch to fetching all threads via gql
      .addCase(
        fetchLimitedInitialChatState.pending,
        handleFetchUserThreadsStartingReducer,
      )
      .addCase(
        fetchLimitedInitialChatState.fulfilled,
        handleFetchUserThreadsCompleteReducer,
      )
      .addCase(
        fetchLimitedInitialChatState.rejected,
        handleFetchUserThreadsErrorReducer,
      )
      .addCase(fetchThreadState.pending, handleFetchThreadStartingReducer)
      .addCase<any>(
        fetchThreadState.fulfilled,
        handleFetchThreadCompleteReducer,
      )
      .addCase(fetchThreadState.rejected, handleFetchThreadErrorReducer);
  },
  selectors: {
    getThreadById: getThreadByIdSelector,
    getOrderedThreads: getOrderedThreadsSelector,
    getMessagesForThread: getMessagesForThreadSelector,
    getThreadLoadingState: getThreadLoadingStateSelector,
  },
});

export const {
  getThreadById,
  getMessagesForThread,
  getOrderedThreads,
  getThreadLoadingState,
} = chatStoreSlice.selectors;
export const {
  createThread,
  deleteThread,
  sendMessage,
  updateMessage,
  updateThread,
} = chatStoreSlice.actions;
export const chatStoreReducer = chatStoreSlice.reducer;
